import React, { useState, useEffect, useRef, useCallback } from "react";
import { trpc } from "../lib/trpc.js";
import { RichMarkdown } from "../components/RichContent.js";
import {
  Send, Bot, User, Loader2, MessageSquare, Sparkles, Trash2,
  BarChart3, Globe, Workflow, Plus, Paperclip, FileText,
  X, Settings2, Zap, Image as ImageIcon, Music, Video,
  File, Database, GitBranch, Cpu, Eye, ZoomIn, ChevronDown,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: number;
}

interface UploadedFile {
  fileId: string;
  name: string;
  size: number;
  type: string;
  previewUrl?: string; // blob URL para imagens
  uploading?: boolean;
  error?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SUGGESTIONS = [
  { icon: Bot,       text: "Qual o status dos agentes?" },
  { icon: BarChart3, text: "Resumo de receita da semana" },
  { icon: Globe,     text: "Quais sites estão ativos?" },
  { icon: Workflow,  text: "Crie um novo pipeline" },
];

const YOS_AGENTS = [
  { id: "", label: "Auto (Megan decide)" },
  { id: "megan", label: "Megan (Orquestradora)" },
  { id: "funnel_builder", label: "Funnel Builder" },
  { id: "content_processor", label: "Content Processor" },
  { id: "strategist", label: "Strategist" },
  { id: "critic", label: "Critic" },
  { id: "ads_manager", label: "Ads Manager" },
  { id: "monitor", label: "Monitor" },
  { id: "performance_optimizer", label: "Performance Optimizer" },
  { id: "creatives", label: "Creatives" },
  { id: "publish_manager", label: "Publish Manager" },
  { id: "seo_specialist", label: "SEO Specialist" },
  { id: "data_analyst", label: "Data Analyst" },
];

const YOS_SKILLS = [
  { name: "site-deployer", desc: "Deploy de sites" },
  { name: "content-gen", desc: "Geração de conteúdo" },
  { name: "seo-audit", desc: "Auditoria SEO" },
  { name: "ad-launcher", desc: "Lançamento de ads" },
  { name: "image-gen", desc: "Geração de imagens" },
  { name: "analytics", desc: "Analytics" },
  { name: "ymonetize-brain-os", desc: "Brain OS" },
  { name: "ymonetize-frontend-developer", desc: "Frontend Dev" },
  { name: "ymonetize-devops-engineer", desc: "DevOps" },
  { name: "ymonetize-system-architect", desc: "Arquitetura" },
];

// Conectores configurados com acesso via Vault YOS
const YOS_CONNECTORS = [
  {
    id: "github",
    label: "GitHub",
    desc: "Repos YOS",
    icon: GitBranch,
    color: "text-gray-400",
    bg: "bg-gray-500/10",
    border: "border-gray-500/20",
    configured: true, // token no vault
    category: "Desenvolvimento",
  },
  {
    id: "railway",
    label: "Railway",
    desc: "Deploy & Infra",
    icon: Cpu,
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
    configured: true, // token no vault
    category: "Infraestrutura",
  },
  {
    id: "voyage_ai",
    label: "Voyage AI",
    desc: "RAG & Embeddings",
    icon: Zap,
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    configured: true, // key no vault
    category: "IA",
  },
  {
    id: "yos_db",
    label: "YOS Database",
    desc: "MySQL Brain DB",
    icon: Database,
    color: "text-success",
    bg: "bg-success/10",
    border: "border-success/20",
    configured: true, // conn string no vault
    category: "Banco de Dados",
  },
  {
    id: "cloudflare",
    label: "Cloudflare",
    desc: "DNS & Workers",
    icon: Globe,
    color: "text-success",
    bg: "bg-success/10",
    border: "border-success/20",
    configured: false,
    category: "Infraestrutura",
  },
  {
    id: "google_drive",
    label: "Google Drive",
    desc: "Documentos",
    icon: File,
    color: "text-yellow-400",
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/20",
    configured: false,
    category: "Produtividade",
  },
];

// ─── File Preview Component ───────────────────────────────────────────────────

function FilePreviewCard({
  file,
  onRemove,
  onPreview,
}: {
  file: UploadedFile;
  onRemove: () => void;
  onPreview: (url: string, name: string) => void;
}) {
  const isImage = file.type.startsWith("image/");
  const isAudio = file.type.startsWith("audio/");
  const isVideo = file.type.startsWith("video/");
  const isPdf = file.type === "application/pdf";

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  }

  function getIcon() {
    if (isAudio) return <Music className="w-5 h-5 text-purple-400" />;
    if (isVideo) return <Video className="w-5 h-5 text-pink-400" />;
    if (isPdf) return <FileText className="w-5 h-5 text-red-400" />;
    return <File className="w-5 h-5 text-blue-400" />;
  }

  return (
    <div className={`relative group rounded-xl border overflow-hidden transition-all duration-150 ${
      file.error
        ? "border-red-500/30 bg-red-500/5"
        : file.uploading
        ? "border-primary/30 bg-primary/5"
        : "border-border bg-secondary/50 hover:border-primary/30"
    }`}>
      {/* Image preview */}
      {isImage && file.previewUrl ? (
        <div
          className="relative w-24 h-24 cursor-pointer"
          onClick={() => onPreview(file.previewUrl!, file.name)}
        >
          <img
            src={file.previewUrl}
            alt={file.name}
            className="w-full h-full object-cover"
          />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <ZoomIn className="w-5 h-5 text-white" />
          </div>
          {/* Upload progress overlay */}
          {file.uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </div>
          )}
        </div>
      ) : (
        /* Non-image preview */
        <div className="flex items-center gap-2.5 px-3 py-2.5 min-w-[140px] max-w-[200px]">
          <div className="shrink-0">
            {file.uploading ? (
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
            ) : file.error ? (
              <X className="w-5 h-5 text-red-400" />
            ) : (
              getIcon()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-foreground truncate">{file.name}</p>
            <p className="text-[10px] text-muted-foreground">
              {file.error ? (
                <span className="text-red-400">Erro no upload</span>
              ) : file.uploading ? (
                <span className="text-primary">Enviando...</span>
              ) : (
                formatSize(file.size)
              )}
            </p>
          </div>
        </div>
      )}

      {/* Remove button */}
      {!file.uploading && (
        <button
          onClick={onRemove}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-background/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:border-red-500 hover:text-white text-foreground"
          title="Remover arquivo"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Name below image */}
      {isImage && file.previewUrl && (
        <div className="px-2 py-1 bg-background/80 border-t border-border">
          <p className="text-[10px] text-muted-foreground truncate max-w-[88px]">{file.name}</p>
        </div>
      )}
    </div>
  );
}

// ─── Image Preview Modal ──────────────────────────────────────────────────────

function ImagePreviewModal({
  url,
  name,
  onClose,
}: {
  url: string;
  name: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={url}
          alt={name}
          className="max-w-full max-h-[85vh] object-contain"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
          <p className="text-white text-sm font-medium truncate">{name}</p>
        </div>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Main Chat Component ──────────────────────────────────────────────────────

export function Chat() {
  const [input, setInput] = useState("");
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedConnectors, setSelectedConnectors] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const [advancedTab, setAdvancedTab] = useState<"agents" | "connectors">("agents");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const { data: conversations } = trpc.chat.listConversations.useQuery();
  const { data: chatMessages, isLoading: loadingMessages } = trpc.chat.getMessages.useQuery(
    { conversationId: activeConversationId! },
    { enabled: !!activeConversationId }
  );

  const sendMessage = trpc.chat.sendMessage.useMutation({
    onSuccess: (data) => {
      if (data.conversationId && !activeConversationId) {
        setActiveConversationId(data.conversationId);
      }
      utils.chat.getMessages.invalidate();
      utils.chat.listConversations.invalidate();
    },
  });

  const deleteConversation = trpc.chat.deleteConversation.useMutation({
    onSuccess: () => {
      setActiveConversationId(null);
      utils.chat.listConversations.invalidate();
    },
  });

  const uploadFileMutation = trpc.brain.uploadFile.useMutation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach(f => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Create preview URL for images
      const previewUrl = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined;

      const tempId = `uploading-${Date.now()}-${Math.random()}`;

      // Add file in uploading state
      setUploadedFiles(prev => [...prev, {
        fileId: tempId,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl,
        uploading: true,
      }]);

      try {
        // Get presigned URL from Manus API
        const result = await uploadFileMutation.mutateAsync({ fileName: file.name });

        // Upload to presigned URL
        await fetch(result.upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });

        // Update with real fileId
        setUploadedFiles(prev => prev.map(f =>
          f.fileId === tempId
            ? { ...f, fileId: result.file_id, uploading: false }
            : f
        ));
      } catch (err) {
        console.error("Upload error:", err);
        setUploadedFiles(prev => prev.map(f =>
          f.fileId === tempId
            ? { ...f, uploading: false, error: "Falha no upload" }
            : f
        ));
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [uploadFileMutation]);

  function removeFile(fileId: string) {
    setUploadedFiles(prev => {
      const file = prev.find(f => f.fileId === fileId);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return prev.filter(f => f.fileId !== fileId);
    });
  }

  const handleSend = async () => {
    const readyFiles = uploadedFiles.filter(f => !f.uploading && !f.error);
    if (!input.trim() && readyFiles.length === 0) return;
    if (sendMessage.isPending) return;

    const message = input.trim() || `[${readyFiles.length} arquivo(s) anexado(s)]`;

    // Build enhanced message with context
    let enhancedMessage = message;
    if (selectedAgent) {
      enhancedMessage = `[Agente: ${selectedAgent}] ${enhancedMessage}`;
    }
    if (selectedSkills.length > 0) {
      enhancedMessage = `[Skills: ${selectedSkills.join(", ")}] ${enhancedMessage}`;
    }
    if (selectedConnectors.length > 0) {
      enhancedMessage = `[Conectores: ${selectedConnectors.join(", ")}] ${enhancedMessage}`;
    }

    const fileIds = readyFiles.map(f => f.fileId);

    setInput("");
    // Cleanup preview URLs
    uploadedFiles.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setUploadedFiles([]);

    await sendMessage.mutateAsync({
      conversationId: activeConversationId ?? undefined,
      message: enhancedMessage,
      selectedAgent: selectedAgent || undefined,
      selectedSkills: selectedSkills.length > 0 ? selectedSkills : undefined,
      fileIds: fileIds.length > 0 ? fileIds : undefined,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setActiveConversationId(null);
    setInput("");
  };

  const isUploading = uploadedFiles.some(f => f.uploading);
  const hasReadyFiles = uploadedFiles.some(f => !f.uploading && !f.error);
  const messages: Message[] = (chatMessages || []) as Message[];

  return (
    <>
      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal
          url={previewImage.url}
          name={previewImage.name}
          onClose={() => setPreviewImage(null)}
        />
      )}

      <div className="flex h-[calc(100vh-4rem)] gap-4">
        {/* Sidebar — Conversations */}
        <div className="w-60 flex-shrink-0 bg-card border border-border rounded-xl flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border">
            <button
              onClick={handleNewChat}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 shadow-md shadow-primary/20"
            >
              <Plus className="w-4 h-4" />
              Nova Conversa
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {conversations?.map((conv) => (
              <div
                key={conv.id}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 ${
                  activeConversationId === conv.id
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                }`}
                onClick={() => setActiveConversationId(conv.id)}
              >
                <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-xs truncate flex-1">{conv.title || "Sem título"}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation.mutate({ conversationId: conv.id });
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-400 transition-all rounded"
                  aria-label="Excluir conversa"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {(!conversations || conversations.length === 0) && (
              <div className="text-center py-8 px-3">
                <MessageSquare className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Nenhuma conversa ainda</p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-card border border-border rounded-xl flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-3 bg-card/80 backdrop-blur-sm">
            <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center shadow-md shadow-primary/30">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-sm leading-tight">YOS - Megan AI</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
                <span className="text-[11px] text-muted-foreground">Online · Brain OS v3.0</span>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">
              {selectedAgent && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                  <Bot className="w-2.5 h-2.5" />{selectedAgent}
                </span>
              )}
              {selectedConnectors.map(c => (
                <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20">
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {!activeConversationId && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/70/20 rounded-full flex items-center justify-center mb-5 shadow-xl shadow-primary/10">
                  <Sparkles className="w-9 h-9 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Olá! Eu sou a Megan</h3>
                <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                  Sua assistente de IA do ecossistema YOS. Posso ajudar com análises,
                  relatórios, status dos agentes e muito mais.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-6 max-w-sm w-full">
                  {SUGGESTIONS.map(({ icon: Icon, text }) => (
                    <button
                      key={text}
                      onClick={() => setInput(text)}
                      className="flex items-center gap-2 px-3 py-2.5 bg-secondary hover:bg-secondary/80 border border-border hover:border-primary/30 rounded-xl text-xs text-muted-foreground hover:text-foreground transition-all duration-150 text-left"
                    >
                      <Icon className="w-3.5 h-3.5 text-primary shrink-0" />
                      <span className="truncate">{text}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loadingMessages && (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={"flex gap-3 animate-fade-in " + (msg.role === "user" ? "flex-row-reverse" : "")}
              >
                <div className={"w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 " + (
                  msg.role === "user"
                    ? "bg-secondary border border-border"
                    : "bg-gradient-to-br from-primary to-primary/70 shadow-md shadow-primary/20"
                )}>
                  {msg.role === "user" ? (
                    <User className="w-4 h-4 text-secondary-foreground" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  )}
                </div>
                <div className={"max-w-[72%] flex flex-col gap-1 " + (msg.role === "user" ? "items-end" : "items-start")}>
                  <div className={"px-4 py-2.5 rounded-2xl text-sm leading-relaxed " + (
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md shadow-md shadow-primary/20"
                      : "bg-secondary text-secondary-foreground rounded-bl-md border border-border"
                  )}>
                    {msg.role === "user" ? (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    ) : (
                      <RichMarkdown content={msg.content} />
                    )}
                  </div>
                  <p className={"text-[10px] text-muted-foreground/70 px-1 " + (msg.role === "user" ? "text-right" : "")}>
                    {new Date(msg.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            ))}

            {sendMessage.isPending && (
              <div className="flex gap-3 animate-fade-in">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0 shadow-md shadow-primary/20">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-secondary rounded-2xl rounded-bl-md border border-border px-4 py-3">
                  <div className="flex gap-1 items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-border space-y-2">

            {/* File Previews */}
            {uploadedFiles.length > 0 && (
              <div className="flex items-start gap-2 flex-wrap p-3 bg-background/50 border border-border rounded-xl">
                <div className="flex items-center gap-2 flex-wrap flex-1">
                  {uploadedFiles.map((f) => (
                    <FilePreviewCard
                      key={f.fileId}
                      file={f}
                      onRemove={() => removeFile(f.fileId)}
                      onPreview={(url, name) => setPreviewImage({ url, name })}
                    />
                  ))}
                </div>
                {/* Clear all button */}
                {uploadedFiles.length > 1 && !isUploading && (
                  <button
                    onClick={() => {
                      uploadedFiles.forEach(f => {
                        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
                      });
                      setUploadedFiles([]);
                    }}
                    className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-destructive/10 transition-colors self-start"
                  >
                    <X className="w-3 h-3" />
                    Limpar todos
                  </button>
                )}
              </div>
            )}

            {/* Upload progress indicator */}
            {isUploading && (
              <div className="flex items-center gap-2 text-[11px] text-primary px-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Enviando {uploadedFiles.filter(f => f.uploading).length} arquivo(s)...</span>
              </div>
            )}

            {/* Advanced panel */}
            {showAdvanced && (
              <div className="p-3 bg-background/50 border border-border rounded-xl space-y-3">
                {/* Tabs */}
                <div className="flex gap-1 bg-secondary/50 rounded-lg p-0.5">
                  <button
                    onClick={() => setAdvancedTab("agents")}
                    className={"flex-1 text-[11px] font-medium py-1.5 rounded-md transition-all " + (
                      advancedTab === "agents"
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Agentes & Skills
                  </button>
                  <button
                    onClick={() => setAdvancedTab("connectors")}
                    className={"flex-1 text-[11px] font-medium py-1.5 rounded-md transition-all " + (
                      advancedTab === "connectors"
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Conectores
                    {selectedConnectors.length > 0 && (
                      <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold">
                        {selectedConnectors.length}
                      </span>
                    )}
                  </button>
                </div>

                {advancedTab === "agents" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Agente YOS</label>
                      <select
                        value={selectedAgent}
                        onChange={(e) => setSelectedAgent(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50"
                      >
                        {YOS_AGENTS.map(a => (
                          <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Skills YOS</label>
                      <div className="max-h-28 overflow-y-auto space-y-1 bg-background border border-input rounded-lg p-2">
                        {YOS_SKILLS.map((skill) => (
                          <label key={skill.name} className="flex items-center gap-2 text-[11px] text-foreground cursor-pointer hover:bg-secondary/50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={selectedSkills.includes(skill.name)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedSkills(prev => [...prev, skill.name]);
                                else setSelectedSkills(prev => prev.filter(s => s !== skill.name));
                              }}
                              className="rounded border-border text-primary focus:ring-primary/50"
                            />
                            <span>{skill.name}</span>
                            <span className="text-muted-foreground/60 ml-auto text-[10px]">{skill.desc}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {advancedTab === "connectors" && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground">
                      Selecione os conectores para usar nesta conversa. Conectores com
                      <span className="text-success mx-1">●</span>já estão configurados no Vault YOS.
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {YOS_CONNECTORS.map((conn) => {
                        const Icon = conn.icon;
                        const isSelected = selectedConnectors.includes(conn.id);
                        return (
                          <button
                            key={conn.id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedConnectors(prev => prev.filter(c => c !== conn.id));
                              } else {
                                setSelectedConnectors(prev => [...prev, conn.id]);
                              }
                            }}
                            className={"flex items-center gap-2 px-2.5 py-2 rounded-lg border text-left transition-all duration-150 " + (
                              isSelected
                                ? conn.bg + " " + conn.border + " " + conn.color
                                : "border-border bg-background/50 text-muted-foreground hover:border-border/80 hover:text-foreground"
                            )}
                          >
                            <Icon className={"w-3.5 h-3.5 shrink-0 " + (isSelected ? conn.color : "")} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <span className="text-[11px] font-medium truncate">{conn.label}</span>
                                {conn.configured && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-success shrink-0" title="Configurado no Vault" />
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground/70 truncate">{conn.desc}</p>
                            </div>
                            {isSelected && (
                              <div className="w-3.5 h-3.5 rounded-full border-2 border-current flex items-center justify-center shrink-0">
                                <div className="w-1.5 h-1.5 rounded-full bg-current" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Active selections summary */}
                {(selectedAgent || selectedSkills.length > 0 || selectedConnectors.length > 0) && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-border/50">
                    {selectedAgent && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                        <Bot className="w-2.5 h-2.5" />{selectedAgent}
                        <button onClick={() => setSelectedAgent("")} className="hover:text-destructive ml-0.5"><X className="w-2.5 h-2.5" /></button>
                      </span>
                    )}
                    {selectedSkills.map(s => (
                      <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20 flex items-center gap-1">
                        <Zap className="w-2.5 h-2.5" />{s}
                        <button onClick={() => setSelectedSkills(prev => prev.filter(x => x !== s))} className="hover:text-destructive ml-0.5"><X className="w-2.5 h-2.5" /></button>
                      </span>
                    ))}
                    {selectedConnectors.map(c => {
                      const conn = YOS_CONNECTORS.find(x => x.id === c);
                      return (
                        <span key={c} className={"text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 " + (conn ? conn.bg + " " + conn.color + " " + conn.border : "bg-success/10 text-success border-success/20")}>
                          {c}
                          <button onClick={() => setSelectedConnectors(prev => prev.filter(x => x !== c))} className="hover:text-destructive ml-0.5"><X className="w-2.5 h-2.5" /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Input bar */}
            <div className="flex items-end gap-2 bg-secondary/70 border border-border rounded-xl px-3 py-2 focus-within:border-primary/40 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xlsx,.xls,.pptx,.zip,.mp3,.mp4,.webm,.wav,.png,.jpg,.jpeg,.gif,.webp"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary disabled:opacity-40"
                title="Anexar arquivo (imagens, documentos, vídeos, áudio)"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <Paperclip className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={"p-1.5 transition-colors rounded-lg " + (showAdvanced ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary")}
                title="Agentes, Skills, Conectores"
              >
                <Settings2 className="w-4 h-4" />
              </button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pergunte algo à Megan..."
                rows={1}
                className="flex-1 bg-transparent text-foreground text-sm resize-none outline-none py-1.5 max-h-32 placeholder:text-muted-foreground"
                style={{ minHeight: "36px" }}
              />
              {/* File count badge */}
              {hasReadyFiles && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-medium shrink-0">
                  {uploadedFiles.filter(f => !f.uploading && !f.error).length} arquivo(s)
                </span>
              )}
              <button
                onClick={handleSend}
                disabled={(!input.trim() && !hasReadyFiles) || sendMessage.isPending || isUploading}
                className="p-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-40 shadow-md shadow-primary/20"
                aria-label="Enviar mensagem"
              >
                {sendMessage.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground/70 text-center">
              Enter para enviar · Shift+Enter para nova linha · <Paperclip className="w-2.5 h-2.5 inline" /> para anexar · <Settings2 className="w-2.5 h-2.5 inline" /> para agentes/conectores
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
